import React from 'react';
import { useTranslation } from 'react-i18next';
import {useSelector} from '../../reduxHooks';
import { useHistory } from 'react-router-dom';
import {Alert, Button} from '@betareader/design-system';
import AppIcon from '../../components/AppIcon';
import styled from 'styled-components';
import {
  CritiqueGroup,
  useCompleteCheckpoint,
} from 'src/hooks/useCritiqueGroups';
import {
  activeMembers,
  findMembership,
  getCheckpointState,
  groupSpineCoverUrl,
  isRequired,
  isSelfMarkable,
  sortCheckpoints,
  userRefId,
  userRefName,
} from 'src/utils/critiqueGroupUtil';
import { MediaQuery } from 'src/constants/size';
import { BookSpine } from 'src/components/critiqueGroups/bookSpine';
import {
  CheckpointTimeline,
  MarkDoneButton,
} from 'src/components/critiqueGroups/checkpointTimeline';
import { CritiqueAvatarStack } from 'src/components/critiqueGroups/critiqueAvatar';
import { CritiqueEmptyState } from 'src/components/critiqueGroups/critiqueStates';

/**
 * Design F1 — the member's group home: the book, their own checkpoints, and the
 * writing family.
 *
 * Self-marks are the ONLY completion signal the client owns; read/comment/survey
 * deliverables are tracked server-side and the member-facing API doesn't expose
 * a per-member `met` (the completion overview is host-only, by design). So a
 * row is shown as done only when we can prove it from `checkpointCompletions`,
 * and auto-tracked ones are described rather than asserted — better than
 * claiming a state we'd be guessing at.
 */

const Hero = styled.div`
  padding: 26px 30px;
  display: flex;
  gap: 22px;
  background: var(--br-surface);
  border: 1px solid #e7e3d9;
  border-radius: 13px;
  margin-bottom: 20px;
  ${MediaQuery.phone} {
    padding: 18px;
    flex-direction: column;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1.3fr 1fr;
  gap: 20px;
  ${MediaQuery.phone} {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background: var(--br-surface);
  border: 1px solid #e7e3d9;
  border-radius: 13px;
  padding: 16px;
`;

const CardTitle = styled.h4`
  margin: 0;
  font-size: 15px;
  font-weight: 800;
`;

const CaughtUp = styled.span<{ $ok: boolean }>`
  font-size: 11px;
  font-weight: 700;
  padding: 3px 9px;
  border-radius: 999px;
  white-space: nowrap;
  color: ${({ $ok }) => ($ok ? '#2fa84f' : '#c88a1a')};
  background: var(--br-raised-surface);
`;

const Tag = styled.span`
  font-size: 11px;
  font-weight: 800;
  color: var(--br-accent);
  background: var(--br-raised-surface);
  padding: 3px 9px;
  border-radius: 999px;
  letter-spacing: 0.04em;
`;

type Props = {
  group: CritiqueGroup;
  onOpenTab: (tab: any) => void;
  /** Renders only the checkpoint timeline (the Checkpoints tab reuses this). */
  checkpointsOnly?: boolean;
};

const MemberHome: React.FC<Props> = ({ group, onOpenTab, checkpointsOnly }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const userProfile = useSelector((state: any) => state.user.userProfile);
  const uid = userProfile?._id;
  // useCompleteCheckpoint already invalidates the group query, which the
  // detail container observes — no manual refetch needed here.
  const complete = useCompleteCheckpoint(group._id);

  const membership = findMembership(group, uid);
  // `checkpointCompletions` is on the membership: the member's own self-marks.
  const completions: Array<{ checkpoint: string }> =
    (membership as any)?.checkpointCompletions || [];
  const selfMarked = new Set(completions.map(c => String(c.checkpoint)));

  const checkpoints = sortCheckpoints(group.checkpoints || []);
  const members = activeMembers(group);
  const firstBook = (group.books || [])[0];
  const bookTitle = typeof firstBook === 'string' ? undefined : firstBook?.title;
  const bookId = typeof firstBook === 'string' ? firstBook : firstBook?._id;

  const markDone = (checkpointId: string) => complete.mutate({ checkpointId });

  const meta = Object.fromEntries(
    checkpoints.map(cp => {
      const done = selfMarked.has(cp._id);
      return [
        cp._id,
        {
          done,
          action: isSelfMarkable(cp) ? (
            <MarkDoneButton
              done={done}
              loading={complete.isPending}
              onClick={() => markDone(cp._id)}
            />
          ) : undefined,
        },
      ];
    })
  );

  // "Caught up" counts only what we can prove: a required, self-markable
  // checkpoint that's overdue and unmarked. Auto-tracked ones are never
  // asserted as behind, since the member API doesn't tell us.
  const behind = checkpoints.filter(
    cp =>
      isSelfMarkable(cp) &&
      isRequired(cp) &&
      !selfMarked.has(cp._id) &&
      getCheckpointState(cp, { done: false }) === 'overdue'
  );
  const caughtUp = behind.length === 0;

  const timeline = (
    <Card>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 10,
          gap: 8,
        }}
      >
        <CardTitle>{t('CritiqueMyCheckpoints', 'My checkpoints')}</CardTitle>
        {checkpoints.length > 0 && (
          <CaughtUp $ok={caughtUp}>
            {caughtUp
              ? t('CritiqueCaughtUp', 'You’re caught up 🎉')
              : t('CritiqueNBehind', '{{count}} to catch up on', {
                  count: behind.length,
                })}
          </CaughtUp>
        )}
      </div>
      {complete.isError && (
        <Alert variant='error' data-testid="member-mark-error">
          {complete.error?.message}
        </Alert>
      )}
      <CheckpointTimeline
        checkpoints={checkpoints}
        meta={meta}
        emptyState={
          <CritiqueEmptyState
            data-testid="member-checkpoints-empty"
            icon="flag checkered"
            title={t('CritiqueNoCpMemberTitle', 'No checkpoints yet')}
            body={t(
              'CritiqueNoCpMemberBody',
              'Your host hasn’t set a pace yet — read at your own for now.'
            )}
          />
        }
      />
    </Card>
  );

  if (checkpointsOnly) return <div data-testid="member-checkpoints">{timeline}</div>;

  return (
    <div data-testid="member-home">
      <Hero>
        <BookSpine
          title={bookTitle}
          coverUrl={groupSpineCoverUrl(group)}
          seed={group._id}
          width={96}
          height={138}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <Tag>
            {t('CritiqueReadingAsPartOf', 'READING AS PART OF · {{group}}', {
              group: group.name.toUpperCase(),
            })}
          </Tag>
          <h3 style={{ margin: '10px 0 3px', fontSize: 23, fontWeight: 800 }}>
            {bookTitle || t('CritiqueNoBookLinked', 'No book linked yet')}
          </h3>
          <p style={{ margin: 0, fontSize: 13.5, color: '#8a857b' }}>
            {t('CritiqueHostedByAuthor', 'Hosted by the author')}
          </p>
          {bookId ? (
            <div style={{ display: 'flex', gap: 10, marginTop: 16, flexWrap: 'wrap' }}>
              {/* Into the existing reader — this feature never re-implements it.
                  The reader is `exact path="/read"` and takes the book as a
                  QUERY param (reader/index.js reads location.search.book), so
                  `/read/<id>` would 404. */}
              <Button
                onClick={() => history.push(`/read?book=${bookId}`)}
                style={{ borderRadius: 10, fontWeight: 700, padding: '11px 20px' }}
                data-testid="member-start-reading"
              >
                <AppIcon name="book" aria-hidden='true' />
                {t('CritiqueStartReading', 'Start reading')}
              </Button>
              <Button
                variant='secondary'
                onClick={() => onOpenTab('feedback')}
                style={{ borderRadius: 10 }}
                data-testid="member-open-feedback"
              >
                {t('CritiqueSeeCircleFeedback', 'Feedback from the circle')}
              </Button>
            </div>
          ) : (
            <Alert style={{ marginTop: 14 }}>
              {t(
                'CritiqueNoBookYetMember',
                'The host hasn’t linked a manuscript yet. Hang tight — reading opens when they do.'
              )}
            </Alert>
          )}
        </div>
      </Hero>

      <Grid>
        {timeline}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Card>
            <CardTitle style={{ marginBottom: 12 }}>
              {t('CritiqueYourWritingFamily', 'Your writing family')}
            </CardTitle>
            <CritiqueAvatarStack
              size={34}
              max={6}
              people={members.map(m => ({
                id: userRefId(m.user),
                name: userRefName(m.user),
              }))}
            />
            <p style={{ margin: '12px 0 0', fontSize: 12, color: '#8a857b' }}>
              {t('CritiqueNReadersHosted', '{{count}} readers · hosted by the author', {
                count: members.length,
              })}
            </p>
          </Card>
          <Card>
            <CardTitle style={{ marginBottom: 8 }}>
              {t('CritiqueTabDiscussion', 'Discussion')}
            </CardTitle>
            <p style={{ margin: 0, fontSize: 12.5, color: '#6b675e', lineHeight: 1.5 }}>
              {t(
                'CritiqueWallTeaser',
                'Talk about the book with the circle — questions, reactions, and the host’s prompts.'
              )}
            </p>
            <Button
              variant='secondary'
              size="small"
              onClick={() => onOpenTab('discussion')}
              style={{ borderRadius: 7, marginTop: 10 }}
              data-testid="member-open-wall"
            >
              {t('CritiqueOpenWall', 'Open wall')}
            </Button>
          </Card>
        </div>
      </Grid>
    </div>
  );
};

export default MemberHome;
